import { create } from "zustand";

/**
 * Whether the workspace navigation is collapsed.
 *
 * This is a *layout* preference, not org state: it survives reloads, it is the
 * same on every page, and both the sidebar's own collapse button and every page
 * header's expand button drive it. Keeping it in one small store rather than in
 * `OrgShell` state means a page header three routes deep can read it without
 * threading a prop through the router, and a change re-renders only the handful
 * of components that subscribed.
 *
 * The flag is desktop-only by construction: below 768px the sidebar is a fixed
 * drawer with its own scrim and hamburger, and the CSS simply ignores this.
 */

export const SIDEBAR_COLLAPSED_STORAGE_KEY = "rivets.sidebar-collapsed.v1";
/** The inspector's flag: the same kind of preference, for the opposite pane. */
export const INSPECTOR_COLLAPSED_STORAGE_KEY = "rivets.inspector-collapsed.v1";

/** The subset of `Storage` this module needs, so tests can pass a fake. */
export type SidebarStorage = Pick<Storage, "getItem" | "setItem">;

export function readStoredCollapsed(storage: SidebarStorage | undefined, key = SIDEBAR_COLLAPSED_STORAGE_KEY): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function writeStoredCollapsed(storage: SidebarStorage | undefined, collapsed: boolean, key = SIDEBAR_COLLAPSED_STORAGE_KEY): void {
  if (!storage) return;
  try {
    storage.setItem(key, collapsed ? "1" : "0");
  } catch {
    /* Private mode or a full quota: the in-memory value still works. */
  }
}

function browserStorage(): SidebarStorage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

interface SidebarLayoutState {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
  expand: () => void;
  collapse: () => void;
}

const useSidebarLayoutStore = create<SidebarLayoutState>((set) => ({
  collapsed: readStoredCollapsed(browserStorage()),
  setCollapsed: (collapsed) => set((state) => {
    if (state.collapsed === collapsed) return state;
    writeStoredCollapsed(browserStorage(), collapsed);
    return { collapsed };
  }),
  toggle: () => set((state) => {
    const collapsed = !state.collapsed;
    writeStoredCollapsed(browserStorage(), collapsed);
    return { collapsed };
  }),
  expand: () => set((state) => {
    if (!state.collapsed) return state;
    writeStoredCollapsed(browserStorage(), false);
    return { collapsed: false };
  }),
  collapse: () => set((state) => {
    if (state.collapsed) return state;
    writeStoredCollapsed(browserStorage(), true);
    return { collapsed: true };
  }),
}));

/**
 * `{ collapsed, toggle, expand, collapse }` — for the components that need to
 * *read* the flag. It subscribes to `collapsed`, so every one of them re-renders
 * when the navigation is collapsed or expanded; a component that only drives the
 * flag should use `useSidebarLayoutActions` and stay out of that.
 */
export function useSidebarLayout() {
  const collapsed = useSidebarLayoutStore((state) => state.collapsed);
  const { toggle, expand, collapse } = useSidebarLayoutActions();
  return { collapsed, toggle, expand, collapse };
}

/**
 * The actions alone. Each selector returns the same store function forever, so
 * a component that holds only these never re-renders when the flag changes —
 * which is what keeps the sidebar's own collapse button from re-rendering the
 * whole navigation tree on its way out of view.
 */
export function useSidebarLayoutActions() {
  const toggle = useSidebarLayoutStore((state) => state.toggle);
  const expand = useSidebarLayoutStore((state) => state.expand);
  const collapse = useSidebarLayoutStore((state) => state.collapse);
  return { toggle, expand, collapse };
}

/**
 * Whether the workspace inspector (the right-hand pane) is collapsed on a
 * desktop-width window. The navigation's twin: a persisted layout preference,
 * driven from the workspace header and ⌘⌥B. Below 1440px the inspector is an
 * overlay the header opens on demand, and this flag does not apply.
 */
interface InspectorLayoutState {
  collapsed: boolean;
  toggle: () => void;
}

const useInspectorLayoutStore = create<InspectorLayoutState>((set) => ({
  collapsed: readStoredCollapsed(browserStorage(), INSPECTOR_COLLAPSED_STORAGE_KEY),
  toggle: () => set((state) => {
    const collapsed = !state.collapsed;
    writeStoredCollapsed(browserStorage(), collapsed, INSPECTOR_COLLAPSED_STORAGE_KEY);
    return { collapsed };
  }),
}));

export function useInspectorLayout() {
  const collapsed = useInspectorLayoutStore((state) => state.collapsed);
  const toggle = useInspectorLayoutStore((state) => state.toggle);
  return { collapsed, toggle };
}

/**
 * The projects collapsed in the sidebar, per organization (web #13). Stored as
 * the *collapsed* set, so expanded is the default and a new or reordered
 * repository needs no effect to open: the tree used to start every repository
 * collapsed for a frame and re-expand all of them whenever the list changed,
 * undoing what the reader had closed.
 */
export const COLLAPSED_REPOS_STORAGE_KEY = "rivets.sidebar-collapsed-repos.v1";

export function readCollapsedRepos(storage: SidebarStorage | undefined, orgId: string): ReadonlySet<string> {
  if (!storage) return new Set();
  try {
    const value = JSON.parse(storage.getItem(`${COLLAPSED_REPOS_STORAGE_KEY}:${orgId}`) ?? "[]") as unknown;
    return new Set(Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : []);
  } catch {
    return new Set();
  }
}

function writeCollapsedRepos(storage: SidebarStorage | undefined, orgId: string, collapsed: ReadonlySet<string>): void {
  if (!storage) return;
  try {
    storage.setItem(`${COLLAPSED_REPOS_STORAGE_KEY}:${orgId}`, JSON.stringify([...collapsed]));
  } catch {
    /* Private mode or a full quota: the in-memory value still works. */
  }
}

const EMPTY_REPOS: ReadonlySet<string> = new Set();

const useCollapsedReposStore = create<{ byOrg: Record<string, ReadonlySet<string>> }>(() => ({ byOrg: {} }));

function collapsedFor(orgId: string): ReadonlySet<string> {
  return useCollapsedReposStore.getState().byOrg[orgId] ?? peekCollapsed(orgId);
}

export function setRepoCollapsed(orgId: string, repoId: string, collapsed: boolean, storage: SidebarStorage | undefined = browserStorage()): void {
  const current = collapsedFor(orgId);
  if (current.has(repoId) === collapsed) return;
  const next = new Set(current);
  if (collapsed) next.add(repoId);
  else next.delete(repoId);
  writeCollapsedRepos(storage, orgId, next);
  useCollapsedReposStore.setState((state) => ({ byOrg: { ...state.byOrg, [orgId]: next } }));
}

/** The organization's collapsed projects, read synchronously on first use so
 * the first paint already has them. */
export function useCollapsedRepos(orgId: string): ReadonlySet<string> {
  return useCollapsedReposStore((state) => state.byOrg[orgId] ?? (orgId ? peekCollapsed(orgId) : EMPTY_REPOS));
}

/** First read for an organization, cached outside the store until it is
 * written, so the selector stays pure and returns a stable set. */
const peeked = new Map<string, ReadonlySet<string>>();
function peekCollapsed(orgId: string): ReadonlySet<string> {
  let set = peeked.get(orgId);
  if (!set) {
    set = readCollapsedRepos(browserStorage(), orgId);
    peeked.set(orgId, set);
  }
  return set;
}
